import { rpx, RpxResponse } from 'client/lib/rpx-client';
import { defCoursesRoute } from '@course/components/courses-app-router';
import { LoadedProps, RouteLoadProps } from '@components/router';
import { ReadonlyMinidoc } from '@components/minidoc/readonly-minidoc';
import { ProductAndPricePreview } from '../guide-upsells/product-price-preview';
import { BtnPrimary } from '@components/buttons';
import { IcoArrowLeft, IcoCheckCircle } from '@components/icons';
import { useEffect, useState } from 'preact/hooks';
import { showError } from '@components/app-error';
import { Spinner } from '@components/spinner';
import { useRouter } from '@components/router';
import { finalCheckoutPageURL } from '@course/components/course-checkout';
import { loadStripe } from '@stripe/stripe-js';
import { StripeAddress } from 'shared/payments/types';
import * as pmtfmt from 'shared/payments/fmt';
import { StripeContext, StripeInput } from '@course/components/checkout/stripe';
import { reportCheckoutError } from '@course/components/checkout/report-checkout-error';
import { collectUpsellPaymentMethod } from '@course/components/sales-checkout/upsell-cta';
import { CustomUpsellPage } from '@course/components/sales-checkout/custom-upsell-page';

type State = RpxResponse<typeof rpx.postPurchaseUpsell.getState>;
type Offer = State['upsell']['offers'][0];

async function load(route: RouteLoadProps) {
  return rpx.postPurchaseUpsell.getState({ priceId: route.params.priceId });
}

type Props = LoadedProps<typeof load>;

export const route = defCoursesRoute({
  load,
  Page,
  // A checkout pass holder reaches this page straight after paying.
  allowCheckoutPass: true,
});

function OfferView({
  offer,
  card,
  onAccept,
  onReject,
  isProcessing,
}: {
  offer: Offer;
  card?: { last4: string; brand: string };
  onAccept(paymentMethodId?: string, address?: StripeAddress): void;
  onReject(): void;
  isProcessing: boolean;
}) {
  const [stripeCtx, setStripeCtx] = useState<StripeContext | undefined>(undefined);
  const [cardError, setCardError] = useState<string | undefined>(undefined);
  const isFreeOffer = offer.price.paymentType === 'free' || !offer.price.priceInCents;
  // A paid offer with no saved card needs the card form -- accepting without
  // one would send the server hunting for a saved payment method it can't find.
  const needsCardForm = !card && !isFreeOffer;

  const handleAccept = async () => {
    if (!needsCardForm) {
      return onAccept();
    }
    if (!stripeCtx) {
      return;
    }
    setCardError(undefined);
    const result = await collectUpsellPaymentMethod(stripeCtx);
    if ('error' in result) {
      result.error && setCardError(result.error);
      return;
    }
    onAccept(result.paymentMethodId, result.address);
  };

  return (
    <div class="flex flex-col gap-6">
      {offer.content && (
        <div class="prose max-w-none">
          <ReadonlyMinidoc content={offer.content} />
        </div>
      )}
      <div class="flex items-center border rounded-2xl p-4">
        <ProductAndPricePreview
          product={offer.product}
          price={offer.price}
          listPrice={offer.listPrice}
        />
      </div>
      {offer.price.freeTrialPeriod ? (
        <p class="text-sm text-gray-600 text-center font-medium">
          {pmtfmt.price({ priceInCents: 0, currency: offer.price.currency })} today — then{' '}
          {pmtfmt.price(offer.price)}
          {offer.price.interval ? `/${offer.price.interval}` : ''} after your{' '}
          {offer.price.freeTrialPeriod}-day free trial
        </p>
      ) : null}
      {card && (
        <p class="text-sm text-gray-500 text-center">
          Charge to card ending in {card.brand} ····{card.last4}
        </p>
      )}
      {needsCardForm && (
        <div class="text-left">
          <StripeInput
            customerName=""
            priceId={offer.priceId}
            onReady={setStripeCtx}
            collectAddress={true}
            manualPaymentMethod={true}
            disabled={isProcessing}
            amount={offer.price.priceInCents}
            currency={offer.price.currency}
            mode={
              offer.price.freeTrialPeriod
                ? 'setup'
                : offer.price.paymentType === 'subscription' ||
                    offer.price.paymentType === 'paymentplan'
                  ? 'subscription'
                  : 'payment'
            }
          />
          {cardError && (
            <div
              role="alert"
              class="mt-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {cardError}
            </div>
          )}
        </div>
      )}
      <div class="flex flex-col gap-3">
        <BtnPrimary
          class="rounded-full gap-2 pl-6 pr-4 justify-center"
          onClick={handleAccept}
          disabled={isProcessing || (needsCardForm && !stripeCtx)}
        >
          {isProcessing ? (
            <Spinner class="border-white" />
          ) : (
            <>
              <span>{offer.acceptText || 'Yes, add this!'}</span>
              <IcoArrowLeft class="rotate-180" />
            </>
          )}
        </BtnPrimary>
        <button
          class="text-gray-500 hover:text-gray-700 py-2"
          onClick={onReject}
          disabled={isProcessing}
        >
          {offer.rejectText || 'No thanks, continue'}
        </button>
      </div>
    </div>
  );
}

function SuccessMessage({ title }: { title: string }) {
  return (
    <div class="flex flex-col items-center gap-4 py-8">
      <IcoCheckCircle class="size-12 text-green-500" />
      <p class="text-lg font-semibold">{title} added!</p>
    </div>
  );
}

function Page(props: Props) {
  const router = useRouter();
  const {
    upsell,
    card,
    course,
    funnelCampaignId,
    basePriceId,
    stripePublishableKey,
    stripeAccount,
    campaignBrand,
  } = props.state;

  const [offerIndex, setOfferIndex] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [accepted, setAccepted] = useState<string[]>([]);
  const [showSuccess, setShowSuccess] = useState('');
  const [navigatingAway, setNavigatingAway] = useState(false);

  useEffect(() => {
    if (upsell.offers.length && funnelCampaignId) {
      rpx.campaignFunnel
        .recordCampaignFunnelStep({
          courseId: funnelCampaignId,
          eventType: 'upsell_shown',
        })
        .catch(() => undefined);
    }
  }, [upsell.offers.length, funnelCampaignId]);

  if (!upsell.offers.length) {
    let returnUrl: string | undefined;
    try {
      returnUrl = sessionStorage.getItem('postUpsellReturn') || undefined;
      if (returnUrl) {
        sessionStorage.removeItem('postUpsellReturn');
      }
    } catch {
      // sessionStorage unavailable
    }
    if (returnUrl) {
      const sep = returnUrl.includes('?') ? '&' : '?';
      router.goto(`${returnUrl}${sep}upsellAccepted=false`);
    } else if (course) {
      router.goto(finalCheckoutPageURL({ id: course.id, title: course.title }));
    }
    return null;
  }

  if (navigatingAway) {
    return (
      <div class="fixed inset-0 bg-white flex items-center justify-center z-50">
        <div class="flex flex-col items-center gap-3">
          <div class="w-8 h-8 border-3 border-gray-200 border-t-indigo-600 rounded-full animate-spin" />
          <p class="text-gray-500 text-sm">One moment...</p>
        </div>
      </div>
    );
  }

  const currentOffer = upsell.offers[offerIndex];

  function gotoNextOrFinish(justAccepted: boolean) {
    setShowSuccess('');
    const nextIndex = offerIndex + 1;
    if (nextIndex >= upsell.offers.length) {
      let returnUrl: string | undefined;
      try {
        returnUrl = sessionStorage.getItem('postUpsellReturn') || undefined;
        if (returnUrl) {
          sessionStorage.removeItem('postUpsellReturn');
        }
      } catch {
        // sessionStorage unavailable (e.g. Safari private mode)
      }
      setNavigatingAway(true);
      const didAccept = justAccepted || accepted.length > 0;
      if (returnUrl) {
        const sep = returnUrl.includes('?') ? '&' : '?';
        router.goto(`${returnUrl}${sep}upsellAccepted=${didAccept}`);
      } else if (course) {
        router.goto(finalCheckoutPageURL({ id: course.id, title: course.title }));
      }
    } else {
      setOfferIndex(nextIndex);
    }
  }

  async function handleAccept(paymentMethodId?: string, address?: StripeAddress) {
    if (!currentOffer) {
      return;
    }
    setIsProcessing(true);
    try {
      const result = await rpx.postPurchaseUpsell.accept({
        basePriceId,
        offerId: currentOffer.priceId,
        paymentMethodId,
        address,
      });

      if (result.type === 'ok') {
        setAccepted([...accepted, currentOffer.priceId]);
        if (currentOffer.pageConfig?.length) {
          setNavigatingAway(true);
          gotoNextOrFinish(true);
        } else {
          setShowSuccess(currentOffer.product.title);
          setTimeout(() => gotoNextOrFinish(true), 1500);
        }
      } else if (result.type === 'action' && result.clientSecret) {
        const stripe = await loadStripe(stripePublishableKey || '', {
          stripeAccount: stripeAccount || undefined,
        });
        if (stripe) {
          // This uses loadStripe directly (no Payment Element), so
          // billing_details / buildStripeConfirmParams are not needed —
          // Stripe gets billing info from the customer record on the
          // server-created PaymentIntent.
          const { error } = await stripe.confirmPayment({
            clientSecret: result.clientSecret,
            confirmParams: {
              return_url: window.location.href,
            },
            redirect: 'if_required',
          });
          if (error) {
            reportCheckoutError({ step: 'upsell', priceId: currentOffer.priceId, err: error });
            showError({ error: error.message || 'Payment authentication failed.' });
          } else {
            await rpx.postPurchaseUpsell.confirm3ds({
              basePriceId,
              offerId: currentOffer.priceId,
              paymentIntentId: result.clientSecret.split('_secret_')[0],
            });
            setAccepted([...accepted, currentOffer.priceId]);
            setShowSuccess(currentOffer.product.title);
            setTimeout(() => gotoNextOrFinish(true), 1500);
          }
        } else {
          const err = new Error(
            `Your bank's verification didn't load. You haven't been charged for this offer. Refresh the page to try again.`,
          );
          reportCheckoutError({ step: 'upsell', priceId: currentOffer.priceId, err });
          showError(err);
        }
      } else if (result.type === 'error') {
        showError({ error: result.message || 'Payment failed.' });
      }
    } catch (err) {
      reportCheckoutError({ step: 'upsell', priceId: currentOffer?.priceId, err });
      showError(err as Error);
    } finally {
      setIsProcessing(false);
    }
  }

  function handleReject() {
    if (funnelCampaignId) {
      rpx.campaignFunnel
        .recordCampaignFunnelStep({
          courseId: funnelCampaignId,
          eventType: 'upsell_declined',
        })
        .catch(() => undefined);
    }
    gotoNextOrFinish(false);
  }

  if (currentOffer?.pageConfig?.length && !showSuccess) {
    return (
      <CustomUpsellPage
        config={currentOffer.pageConfig}
        courseTitle={course?.title || ''}
        acceptText={currentOffer.acceptText || 'Yes, add this!'}
        declineText={currentOffer.rejectText || 'No thanks, continue'}
        onAccept={handleAccept}
        onDecline={handleReject}
        isProcessing={isProcessing}
        cardInfo={card}
        priceId={currentOffer.priceId}
        trialDays={currentOffer.price.freeTrialPeriod}
        brand={campaignBrand}
        priceDisplay={
          currentOffer.price.freeTrialPeriod
            ? `${pmtfmt.price(currentOffer.price)}${currentOffer.price.interval ? `/${currentOffer.price.interval}` : ''}`
            : undefined
        }
        amount={currentOffer.price.priceInCents}
        currency={currentOffer.price.currency}
        paymentType={currentOffer.price.paymentType}
      />
    );
  }

  return (
    <div class="min-h-screen bg-gray-50 flex flex-col items-center p-4 pt-12">
      <div class="w-full max-w-lg">
        {course && (
          <div class="text-center mb-8">
            <p class="text-sm text-gray-500 mb-1">You just enrolled in</p>
            <h2 class="text-xl font-semibold">{course.title}</h2>
          </div>
        )}

        <div class="bg-white rounded-2xl shadow-lg p-8">
          {upsell.title && <h1 class="text-lg font-semibold text-center mb-6">{upsell.title}</h1>}

          {showSuccess ? (
            <SuccessMessage title={showSuccess} />
          ) : currentOffer ? (
            <OfferView
              offer={currentOffer}
              card={card}
              onAccept={handleAccept}
              onReject={handleReject}
              isProcessing={isProcessing}
            />
          ) : null}
        </div>

        <div class="text-center mt-6">
          <button
            class="text-sm text-gray-400 hover:text-gray-600"
            onClick={() => gotoNextOrFinish(false)}
          >
            Skip and go to course
          </button>
        </div>
      </div>
    </div>
  );
}
